import React, { createContext, ReactNode, useContext } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useSubscription } from '../hooks/useSubscription';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      staleTime: 5000,
      retry: 1,
    },
  },
});

// Create context with default values
export const SubscriptionContext = createContext<ReturnType<typeof useSubscription> | null>(null);

// Provider component
export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <SubscriptionProviderInner>
        {children}
      </SubscriptionProviderInner>
    </QueryClientProvider>
  );
};

// Inner provider that uses the hook
const SubscriptionProviderInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  const subscriptionData = useSubscription();
  
  return (
    <SubscriptionContext.Provider value={subscriptionData}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Custom hook for using the subscription context
export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};
